import { useMemo, useState } from 'react';
import {
  CURRENCY_CATALOGUE,
  formatRate,
  type CurrencyRateCell,
  type CurrencyRateDay,
  type CurrencySettings,
  type WorkspaceCurrency,
} from '@forecastify/shared';
import {
  Badge,
  Icon,
  ListPager,
  Select,
  Skeleton,
  SKELETON_WIDTHS,
  skeletonWidth,
  Tabs,
  TextInput,
  Tooltip,
  type PagerState,
  type SortState,
  type TabItem,
} from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { SettingsPage } from './settings-page';
import { SettingsTable, type SettingsRow } from './settings-table';
import { EditorDialog } from './editor-dialog';
import { Field } from '../card';
import { sortInMemory } from '../sort-in-memory';
import { formatCalendarDate, formatInstant } from '../history/history-labels';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Pénznemek. C51, Phase 4; daily rates FX-1 … FX-8.           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Two tabs: the currencies a workspace works in, and what each was worth on every banking day.
 *
 * ⚠️⚠️ **THE CURRENCY × CURRENCY MATRIX IS GONE (20 Oct 2026).** The product owner asked for *"minden
 * napra lehessen látni hogy mi volt az árfolyam"* — a day per row, a currency per column, every cell
 * editable — and a fourth currency is one more column. The cross rates the matrix drew are still the
 * one division `fxRate` does; nobody types them any more, because every day is stored against the
 * base (`0230`).
 *
 * ⚠️ **FX-8 · the currency tab shows today's rate READ-ONLY.** Every edit happens in the daily table,
 * so there is one place a rate is typed and the latest rate is a consequence of it, not a second
 * input that could disagree with the day it came from.
 *
 * ⚠️ **A plain card table, not `DataList`.** The v3 round's holidays lesson: the shared list brings
 * a search, saved views and filter chips to a screen with four rows and no question to ask of them.
 */

/**
 * The daily table's data and verbs, owned by the route.
 *
 * ⚠️ **Optional on the section**: a host that does not wire the daily table offers no second tab,
 * rather than one that opens onto nothing.
 */
export interface DailyRates {
  /** Until the first answer: skeleton rows under the real headers, no figures. */
  readonly loading: boolean;
  /** The last read failed — the table says so and offers one retry, and shows no figures. */
  readonly failed: boolean;
  readonly days: readonly CurrencyRateDay[];
  /** The server's `codes`: the active non-base currencies, in order — one column each. */
  readonly codes: readonly string[];
  /** ⚠️ ONE key, because the server orders by the first and only the first (`listRates`). */
  readonly sort: SortState;
  readonly onSort: (next: SortState) => void;
  readonly page: PagerState;
  readonly onSet: (date: string, code: string, value: number) => void;
  readonly onRestore: (date: string, code: string) => void;
  readonly onRetry: () => void;
}

export function CurrencySection(props: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly settings: CurrencySettings;
  readonly canManage: boolean;
  readonly syncing: boolean;
  readonly serverError: string | null;
  readonly onCreate: (draft: { code: string; name: string; symbol: string }) => Promise<boolean>;
  readonly onUpdate: (
    currency: WorkspaceCurrency,
    patch: { name?: string; symbol?: string; active?: boolean; rateToBase?: number },
  ) => void;
  readonly onSetDefault: (code: string) => void;
  readonly onDelete: (currency: WorkspaceCurrency) => void;
  readonly onSync: () => void;
  readonly daily?: DailyRates;
}) {
  const t = useT();
  /* 26 Oct 2026 · a save in flight, and whether the last one was refused — the dialog stays
     open until it lands, and shows its refusal inside. */
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  /* ⚠️ Round 5 item 2 — the two cards became two tabs; `tabs` below argues it. */
  const [tab, setTab] = useState<'currencies' | 'fx'>('currencies');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ code: '', name: '', symbol: '' });
  const [sort, setSort] = useState<readonly SortState[]>([]);

  const { currencies } = props.settings;
  const base = currencies.find((one) => one.isDefault) ?? null;
  /**
   * ⚠️⚠️ **EVERY CURRENCY, INCLUDING THE INACTIVE ONES — 22 Sep, AND THIS WAS A TRAP.**
   * The list read `one.active || one.code === base?.code`, so deactivating a currency removed it
   * from the only screen that carries *Aktiválás*: the row's own kebab offered a verb that made
   * the row disappear for ever. The mockup lists them all with a status badge, which is what the
   * badge is for.
   */
  const [query, setQuery] = useState('');
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = currencies.filter(
      (one) =>
        needle === '' ||
        one.code.toLowerCase().includes(needle) ||
        one.name.toLowerCase().includes(needle),
    );
    /**
     * ⚠️ **SORTED IN MEMORY, AND THAT IS RIGHT HERE ONLY.** CLAUDE.md §3 puts sorting on the server
     * for every list that pages; this one never does — a workspace has a handful of currencies and
     * `GET /workspace/currencies` returns every one of them, so there is no page for the sort to lie
     * about. The daily table below is the paged one, and it sends `?sort=`.
     */
    return sortInMemory(matching, sort, {
      code: (one) => one.code,
      name: (one) => one.name,
      rate: (one) => Number(one.rateToBase),
      status: (one) => (one.active ? 0 : 1),
    });
  }, [currencies, query, sort]);

  /* ⚠️ The daily table draws ACTIVE currencies only — an inactive one is still in the list, where
     it can be reactivated, but a column for something nobody prices in is noise. */
  const axis = currencies.filter((one) => one.active);

  /**
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ TWO TABS, NOT TWO CARDS — round 5 item 2.                              │
   * └──────────────────────────────────────────────────────────────────┘
   *
   * The screen stacked a currency list and an FX matrix, each with its own header strip and its own
   * action in the corner — two tables on one page, neither of which is the other's detail. As tabs
   * they are what they always were, two things you look at one at a time.
   *
   * ⚠️⚠️ **AND THE ACTION MOVES ONTO THE TITLE ROW**, `rate-card-section.tsx`'s shape since C31.
   * ⚠️ **Each tab brings its own action**: the currency list adds a currency, the daily table asks
   * the bank now. A single button that changed meaning under the reader would be worse than either.
   *
   * ⚠️ **The daily tab is still absent without a second active currency** — a table with no rate
   * column converts nothing, and the tab would open onto an empty card.
   */
  const offersDaily = props.daily !== undefined && axis.length >= 2;
  const tabs: TabItem[] = [
    {
      value: 'currencies',
      /* ⚠️ The same `globe` the settings rail marks this screen with — a tab and its nav row
         disagreeing about the icon is how somebody loses their place. */
      icon: 'globe',
      label: t('settings.currency.title'),
      ...(props.loading === true ? {} : { count: props.settings.currencies.length }),
    },
    ...(offersDaily
      ? [{ value: 'fx', icon: 'refresh-cw' as const, label: t('fx.tab.daily') }]
      : []),
  ];
  /* ⚠️ Falls back to the currency list when the daily tab is not offered — otherwise deleting a
     currency down to one would leave the screen on a tab that no longer exists. */
  const active = tab === 'fx' && offersDaily ? 'fx' : 'currencies';

  const rows: SettingsRow[] = shown.map((currency) => ({
    id: currency.code,
    cells: [
      { kind: 'badge' as const, text: currency.code, tone: 'outline' as const },
      {
        kind: 'name' as const,
        text: currency.name,
        /* ⚠️ The base currency says so under its name — the mockup's own `sub`. */
        sub: currency.isDefault ? t('settings.currency.default') : undefined,
      },
      /**
       * ⚠️ FX-8 · today's rate, against the base and written with the base's symbol — `395,20 Ft`.
       * The base itself reads `1 Ft`: it is a computed fact rather than an unknown, so not a dash
       * (`state-rules.md` §5), and the symbol answers the *one what* a bare 1 would raise.
       */
      base === null
        ? { kind: 'num' as const, text: '—', className: 'text-muted-foreground' }
        : currency.isDefault
          ? { kind: 'num' as const, text: `1 ${base.symbol}`, className: 'text-muted-foreground' }
          : {
              kind: 'num' as const,
              text: `${formatRate(Number(currency.rateToBase))} ${base.symbol}`,
            },
      {
        kind: 'badge' as const,
        text: currency.active ? t('settings.currency.active') : t('settings.currency.inactive'),
        tone: currency.active ? ('secondary' as const) : ('outline' as const),
        color: currency.active ? 'var(--status-ok)' : 'var(--text-3)',
      },
      /* ⚠️ The base currency has NO menu at all — every one of the three items refers to
         something it cannot be. */
      !props.canManage || currency.isDefault
        ? { kind: 'blank' as const }
        : {
            kind: 'kebab' as const,
            ariaLabel: t('settings.currency.rowMenu', { name: currency.name }),
            items: [
              {
                label: t('settings.currency.makeDefault'),
                icon: 'star' as const,
                onSelect: () => {
                  props.onSetDefault(currency.code);
                },
              },
              {
                label: currency.active
                  ? t('settings.currency.deactivate')
                  : t('settings.currency.activate'),
                icon: 'circle-dot' as const,
                onSelect: () => {
                  props.onUpdate(currency, { active: !currency.active });
                },
              },
              { type: 'separator' as const },
              {
                label: t('common.delete'),
                icon: 'trash' as const,
                destructive: true,
                onSelect: () => {
                  props.onDelete(currency);
                },
              },
            ],
          },
    ],
  }));

  return (
    <div data-screen="settings-currency" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.currency')}
        action={
          !props.canManage
            ? undefined
            : active === 'fx'
              ? {
                  label: props.syncing ? t('fx.syncing') : t('fx.sync'),
                  icon: 'refresh-cw',
                  disabled: props.syncing,
                  onSelect: props.onSync,
                }
              : {
                  label: t('settings.currency.add'),
                  icon: 'plus',
                  onSelect: () => {
                    setAdding(true);
                  },
                }
        }
        tabs={
          <Tabs
            variant="underline"
            className="border-0"
            label={t('settings.nav.currency')}
            value={active}
            onValueChange={(value) => {
              setTab(value === 'fx' ? 'fx' : 'currencies');
            }}
            items={tabs}
          />
        }
        error={props.serverError}
        search={active === 'currencies' ? query : undefined}
        onSearch={active === 'currencies' ? setQuery : undefined}
        searchPlaceholder={t('settings.currency.search')}
      >
        {active === 'fx' && props.daily !== undefined && base !== null ? (
          <DailyTable
            daily={props.daily}
            base={base}
            source={props.settings.source}
            syncedAt={props.settings.syncedAt}
            canManage={props.canManage}
          />
        ) : (
          <SettingsTable
            grid="88px minmax(0,1fr) 140px 104px 36px"
            minWidth={600}
            columns={[
              { label: t('fx.column.currency'), sortKey: 'code' },
              { label: t('fx.column.name'), sortKey: 'name' },
              { label: t('fx.column.today'), align: 'right', sortKey: 'rate' },
              { label: t('settings.currency.column.status'), sortKey: 'status' },
              {},
            ]}
            sort={sort}
            onSortChange={setSort}
            groups={[{ rows }]}
            loading={props.loading}
            empty={{
              icon: 'globe',
              title: t('settings.currency.empty'),
              body: t('settings.currency.emptyBody'),
            }}
          />
        )}
      </SettingsPage>

      <EditorDialog
        open={adding}
        title={t('settings.currency.dialog.new')}
        saveDisabled={draft.code.trim().length !== 3 || draft.name.trim() === ''}
        onClose={() => {
          setSaveFailed(false);
          setAdding(false);
        }}
        busy={saving}
        error={saveFailed ? props.serverError : null}
        onSave={() => {
          setSaving(true);
          void props
            .onCreate({
              code: draft.code.trim().toUpperCase(),
              name: draft.name.trim(),
              symbol:
                draft.symbol.trim() === '' ? draft.code.trim().toUpperCase() : draft.symbol.trim(),
            })
            .then((landed) => {
              setSaving(false);
              setSaveFailed(!landed);
              if (landed) setAdding(false);
            });
        }}
      >
        {/**
         * ⚠️ **A picker AND a free field, which is the mockup's own *"válassz a listából, vagy írd
         * be kézzel"*.** Twelve codes cover what a Hungarian delivery company invoices in; a
         * dropdown of all 180 is a search box with no answer, and refusing the 181st would be a
         * rule nobody asked for.
         */}
        <Field label={t('settings.currency.dialog.known')}>
          <Select
            value=""
            onChange={(event) => {
              const found = CURRENCY_CATALOGUE.find((one) => one.code === event.target.value);
              if (found !== undefined) {
                setDraft({ code: found.code, name: found.name, symbol: found.symbol });
              }
            }}
            options={[
              { value: '', label: t('settings.currency.dialog.pick') },
              ...CURRENCY_CATALOGUE.filter(
                (one) => !currencies.some((have) => have.code === one.code),
              ).map((one) => ({ value: one.code, label: `${one.code} · ${one.name}` })),
            ]}
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-[110px_1fr_110px]">
          <Field label={t('settings.currency.column.code')} required>
            <TextInput
              value={draft.code}
              maxLength={3}
              placeholder="EUR"
              onChange={(event) => {
                setDraft({ ...draft, code: event.target.value.toUpperCase() });
              }}
            />
          </Field>
          <Field label={t('settings.currency.column.name')} required>
            <TextInput
              value={draft.name}
              onChange={(event) => {
                setDraft({ ...draft, name: event.target.value });
              }}
            />
          </Field>
          <Field label={t('settings.currency.column.symbol')}>
            <TextInput
              value={draft.symbol}
              placeholder="€"
              onChange={(event) => {
                setDraft({ ...draft, symbol: event.target.value });
              }}
            />
          </Field>
        </div>
      </EditorDialog>
    </div>
  );
}

/** `395,20`, `395.20`, `1 395,2` → a number; anything else, or zero and below, → null. */
function parseRate(typed: string): number | null {
  const value = Number(typed.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ NAPI ÁRFOLYAMOK — a banking day per row, a currency per column.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE COLUMNS ARE THE SERVER'S `codes`, NOT A LIST WRITTEN HERE.** Adding a fourth currency
 * adds a fourth column with nothing changed in this file — the product owner's own test of the
 * feature (*"ha hozzáadsz egy negyedik devizát akkor eggyel nő az oszlopok száma"*).
 *
 * ⚠️⚠️ **SORTED AND PAGED ON THE SERVER**, CLAUDE.md §3: a workspace with five years of history has
 * some 1,300 banking days, and sorting the 50 on screen would call a page sorted. Every header but
 * *Forrás* sends `?sort=` — the server sorts by `date` or by a currency code and nothing else, so
 * *Forrás* is plain text rather than a button that does nothing.
 *
 * ⚠️ **Not `SettingsTable`**: its grid is fixed per screen and its cells have no slot for a value with
 * a hand-edit mark and a restore beside it. The card, the header strip and the row are copied from
 * it, class for class, so the two tabs read as one screen.
 */
function DailyTable({
  daily,
  base,
  source,
  syncedAt,
  canManage,
}: {
  readonly daily: DailyRates;
  readonly base: WorkspaceCurrency;
  readonly source: CurrencySettings['source'];
  readonly syncedAt: string | null;
  readonly canManage: boolean;
}) {
  const { t, locale } = useI18n();
  const codes = daily.codes;
  const grid = `120px repeat(${String(codes.length)}, minmax(150px, 1fr)) 96px`;
  const minWidth = 120 + codes.length * 160 + 96 + 48;

  /**
   * ⚠️ **TWO STATES PER COLUMN, NOT `toggleSort`'s THREE.** Elsewhere the third click cancels the
   * sort; here a cancelled sort IS *date, newest first* — the server's own fallback — so a third
   * state would be a click that changes nothing on screen. A new column starts ascending, except
   * the date, which starts where the table opens: newest first.
   */
  const sortBy = (key: string): void => {
    if (daily.sort.key === key) {
      daily.onSort({ key, direction: daily.sort.direction === 'asc' ? 'desc' : 'asc' });
      return;
    }
    daily.onSort({ key, direction: key === 'date' ? 'desc' : 'asc' });
  };

  const columns: { key: string | null; label: string; right: boolean }[] = [
    { key: 'date', label: t('fx.column.date'), right: false },
    ...codes.map((code) => ({
      key: code,
      label: t('fx.column.pair', { code, base: base.code }),
      right: true,
    })),
    { key: null, label: t('fx.column.source'), right: false },
  ];

  return (
    <div className="flex flex-col gap-3">
      {/* ⚠️ The subline names the source and when it last answered, which is what makes a figure
          in the table checkable. `source` is `manual` until the first sync; the wording for that is
          the never-synced one, because nothing has been fetched. */}
      <p className="text-muted-foreground text-[12px]">
        {syncedAt === null || source !== 'ecb'
          ? t('fx.subline.never')
          : t('fx.subline.synced', { at: formatInstant(syncedAt, locale) })}
      </p>

      <div
        role="table"
        aria-label={t('fx.table')}
        className="bg-card text-card-foreground overflow-hidden rounded-xl border"
        data-tour="settings-table"
        style={{ minWidth: `${String(minWidth)}px` }}
      >
        <div
          role="row"
          className="bg-muted/40 text-muted-foreground grid items-center gap-2.5 border-b px-3 py-2 text-[11px] font-medium tracking-[0.03em] uppercase"
          style={{ gridTemplateColumns: grid }}
        >
          {columns.map((column) => {
            /* ⚠️ A rate's figure ends 60px before its column does — the 46px ✓ ✕ / restore slot, the
               6px gap and the input's own 8px padding (`RateCell`) — so a right-aligned header ends
               there too, or it sits a slot's width to the right of the numbers it names. */
            const align = column.right ? 'pr-[60px] text-right' : '';
            if (column.key === null) {
              return (
                <span role="columnheader" key="source" className={`truncate ${align}`}>
                  {column.label}
                </span>
              );
            }
            const key = column.key;
            const here = daily.sort.key === key ? daily.sort : undefined;
            return (
              <span
                role="columnheader"
                key={key}
                aria-sort={
                  here === undefined
                    ? 'none'
                    : here.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                }
                className={`min-w-0 ${align}`}
              >
                <button
                  type="button"
                  aria-label={t('list.sortBy', { label: column.label })}
                  onClick={() => {
                    sortBy(key);
                  }}
                  /* ⚠️ `uppercase` restated — preflight resets `text-transform` on a button. */
                  className={`hover:text-foreground inline-flex max-w-full items-center gap-1 tracking-wide uppercase select-none ${
                    column.right ? 'flex-row-reverse' : ''
                  }`}
                >
                  <span className="truncate">{column.label}</span>
                  {here === undefined ? null : (
                    <Icon
                      name={here.direction === 'asc' ? 'chevron-up' : 'chevron-down'}
                      size={12}
                    />
                  )}
                </button>
              </span>
            );
          })}
        </div>

        {!daily.loading ? null : (
          <>
            {SKELETON_WIDTHS.slice(0, 6).map((_widths, index) => (
              <div
                key={`skeleton-${String(index)}`}
                aria-hidden
                className="grid items-center gap-2.5 border-b px-3 last:border-b-0"
                style={{ gridTemplateColumns: grid, minHeight: '42px' }}
              >
                {columns.map((column, at) => (
                  <Skeleton
                    key={column.key ?? 'source'}
                    width={skeletonWidth(index, at)}
                    className={column.right ? 'ml-auto' : undefined}
                  />
                ))}
              </div>
            ))}
            <div role="status" aria-live="polite" className="sr-only">
              {t('state.loading')}
            </div>
          </>
        )}

        {/* ⚠️ A failed read shows no figures (`state-rules.md` §2) and one retry for this table only. */}
        {daily.loading || !daily.failed ? null : (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <p role="alert" className="text-destructive text-[12.5px]">
              {t('fx.error.load')}
            </p>
            <button
              type="button"
              onClick={daily.onRetry}
              className="hover:bg-accent rounded-md border px-3 py-1 text-[12px] font-medium transition-colors"
            >
              {t('fx.retry')}
            </button>
          </div>
        )}

        {daily.loading || daily.failed
          ? null
          : daily.days.map((day) => (
              <DayRow
                key={day.date}
                day={day}
                codes={codes}
                grid={grid}
                canManage={canManage}
                dateLabel={formatCalendarDate(day.date, locale)}
                onSet={daily.onSet}
                onRestore={daily.onRestore}
              />
            ))}

        {daily.loading || daily.failed || daily.days.length > 0 ? null : (
          <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
            <span className="bg-muted text-muted-foreground grid size-11 place-items-center rounded-xl">
              <Icon name="refresh-cw" size={20} />
            </span>
            <div className="mt-1 text-[13px] font-medium">{t('fx.empty')}</div>
            <div className="text-muted-foreground max-w-[380px] text-[12px] leading-relaxed">
              {t('fx.emptyBody')}
            </div>
          </div>
        )}
      </div>

      {/* ⚠️ CLAUDE.md §3's settled pager, `[<] [selector] [>]`, and no count beside it. */}
      <div className="flex items-center justify-end">
        <ListPager
          page={daily.page}
          busy={daily.loading}
          labels={{
            previousPage: t('list.previousPage'),
            nextPage: t('list.nextPage'),
            pageSelector: t('list.pageSelector'),
            pageOf: (page, count) => t('list.pageOf', { page, count }),
          }}
        />
      </div>
    </div>
  );
}

/**
 * One banking day.
 *
 * ⚠️ **The hand-edit mark is per cell AND summarised per row.** Each cell typed by hand carries a
 * dot (and says so to a screen reader); *Forrás* reads *Kézi* when any cell in the row is, *ECB*
 * otherwise. The row summary is what a reader scans down; the dot is which of the columns it means.
 */
function DayRow({
  day,
  codes,
  grid,
  canManage,
  dateLabel,
  onSet,
  onRestore,
}: {
  readonly day: CurrencyRateDay;
  readonly codes: readonly string[];
  readonly grid: string;
  readonly canManage: boolean;
  readonly dateLabel: string;
  readonly onSet: (date: string, code: string, value: number) => void;
  readonly onRestore: (date: string, code: string) => void;
}) {
  const t = useT();
  const manual = codes.some((code) => day.rates[code]?.source === 'manual');

  return (
    <div
      role="row"
      className="hover:bg-accent/40 grid items-center gap-2.5 border-b px-3 py-1.5 transition-colors last:border-0"
      style={{ gridTemplateColumns: grid, minHeight: '42px' }}
    >
      <span role="cell" className="truncate text-[12.5px] font-medium tabular-nums">
        {dateLabel}
      </span>
      {codes.map((code) => (
        <span role="cell" key={code} className="flex min-w-0 justify-end">
          <RateCell
            cell={day.rates[code] ?? null}
            code={code}
            dateLabel={dateLabel}
            canManage={canManage}
            onSet={(value) => {
              onSet(day.date, code, value);
            }}
            onRestore={() => {
              onRestore(day.date, code);
            }}
          />
        </span>
      ))}
      <span role="cell" className="flex min-w-0 items-center">
        <Badge variant={manual ? 'secondary' : 'outline'}>
          {manual ? t('fx.source.manual') : t('fx.source.ecb')}
        </Badge>
      </span>
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE DAY'S RATE — explicit save, `state-rules.md` §8.                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **ENTER OR ✓ SAVES, ESC OR ✕ DISCARDS, AND BLUR SAVES NOTHING.** §8: *"Blur does not save —
 * blur keeps the editor open."* A rate is a figure that re-prices every worklog of its day; tabbing
 * through a row must not write five of them. So leaving the cell keeps the draft, boxed and with
 * its ✓ and ✕ beside it, until somebody decides.
 *
 * ⚠️ **It holds its own draft string** (§8 again): `395,` is not a number and has to be typeable.
 * A refetch underneath — a live invalidation, somebody else's edit — never replaces an open draft
 * (§11); the draft wins until it is saved or discarded.
 *
 * ⚠️ **Borderless at rest, boxed on hover and focus** — the product's standing input rule, which
 * overrides the screenshot's always-boxed cells. A draft keeps its box after blur, because a value
 * that is not saved must not look like one that is.
 */
function RateCell({
  cell,
  code,
  dateLabel,
  canManage,
  onSet,
  onRestore,
}: {
  readonly cell: CurrencyRateCell | null;
  readonly code: string;
  readonly dateLabel: string;
  readonly canManage: boolean;
  readonly onSet: (value: number) => void;
  readonly onRestore: () => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);

  /* ⚠️ No rate for the day: a dash, not a zero and not a control — FX-6, nobody adds a rate to a
     day the bank did not quote in this column. */
  if (cell === null) {
    return (
      <Tooltip content={t('fx.cell.none')}>
        <span
          className="text-muted-foreground pr-[60px] text-[12.5px] tabular-nums"
          aria-label={t('fx.cell.none')}
        >
          —
        </span>
      </Tooltip>
    );
  }

  const shown = formatRate(Number(cell.value));
  const manual = cell.source === 'manual';
  const label = t('fx.cell', { code, date: dateLabel });
  const restorable = manual && cell.bankValue !== null;

  const discard = (): void => {
    setDraft(null);
    setInvalid(false);
  };
  const commit = (): void => {
    if (draft === null) return;
    const value = parseRate(draft);
    if (value === null) {
      setInvalid(true);
      return;
    }
    discard();
    if (value !== Number(cell.value)) onSet(value);
  };

  const mark = !manual ? (
    <span aria-hidden className="size-1.5 shrink-0" />
  ) : (
    <Tooltip content={t('fx.cell.manual')}>
      <span
        className="bg-primary block size-1.5 shrink-0 rounded-full"
        aria-label={t('fx.cell.manual')}
        role="img"
      />
    </Tooltip>
  );

  /* ⚠️ Read-only renders as text with no affordance — `state-rules.md` §3. */
  if (!canManage) {
    return (
      /* The figure ends 60px in, like an editable one (`pr` + the dot and its gap = 60). */
      <span className="flex items-center gap-1.5 pr-[48px]">
        <span className="text-[12.5px] tabular-nums">{shown}</span>
        {mark}
      </span>
    );
  }

  const dirty = draft !== null;

  return (
    <span className="flex items-center gap-1.5">
      {/* A draft that is invalid or not yet saved says so in the kit tooltip. */}
      <InputHint text={invalid ? t('fx.cell.invalid') : dirty ? t('fx.cell.pending') : null}>
        <input
          aria-label={label}
          aria-invalid={invalid || undefined}
          inputMode="decimal"
          value={draft ?? shown}
          onChange={(event) => {
            setDraft(event.target.value);
            setInvalid(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              discard();
            }
          }}
          className={[
            'focus:bg-background h-7 w-[96px] rounded-md border px-2 text-right text-[12.5px] tabular-nums transition-colors outline-none',
            invalid
              ? 'border-destructive'
              : dirty
                ? 'border-ring bg-background'
                : 'hover:border-input focus:border-ring border-transparent bg-transparent',
          ].join(' ')}
        />
      </InputHint>
      {/* ⚠️ A fixed 46px slot, so the column's figures line up whether a cell shows ✓ ✕, a
          restore, or nothing. */}
      <span className="flex w-[46px] shrink-0 items-center gap-0.5">
        {dirty ? (
          <>
            <CellButton
              icon="check"
              label={t('fx.cell.save', { code, date: dateLabel })}
              onClick={commit}
            />
            <CellButton
              icon="x"
              label={t('fx.cell.cancel', { code, date: dateLabel })}
              onClick={discard}
            />
          </>
        ) : (
          /* The hand-edited dot sits right of the figure, before its restore — in the slot, so a
             marked cell's figure lines up with an unmarked one's. */
          <span className="flex items-center gap-1">
            {mark}
            {restorable ? (
              <CellButton
                icon="undo"
                label={t('fx.cell.restore', { code, date: dateLabel })}
                title={t('fx.cell.bank', { value: formatRate(Number(cell.bankValue)) })}
                onClick={onRestore}
              />
            ) : null}
          </span>
        )}
      </span>
    </span>
  );
}

/** The kit tooltip around a cell's input, only while there is something to say. */
function InputHint({
  text,
  children,
}: {
  readonly text: string | null;
  readonly children: React.ReactNode;
}) {
  return text === null ? children : <Tooltip content={text}>{children}</Tooltip>;
}

function CellButton({
  icon,
  label,
  title,
  onClick,
}: {
  readonly icon: 'check' | 'x' | 'undo';
  readonly label: string;
  readonly title?: string;
  readonly onClick: () => void;
}) {
  /* ⚠️ The kit tooltip, never the browser's `title` — the product has one tooltip (CLAUDE.md). */
  return (
    <Tooltip content={title ?? label}>
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className="hover:bg-accent text-muted-foreground hover:text-foreground grid size-[22px] place-items-center rounded-md transition-colors"
      >
        <Icon name={icon} size={13} />
      </button>
    </Tooltip>
  );
}
